import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  ChangeEvent,
} from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./admin.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { RootState, useAppSelector } from "../../store";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import Logs from "../../assets/images/logs.png";
import {
  useGetUserRolesQuery,
  useGetUsersListQuery,
  useUpdateUserMutation,
} from "../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { Input } from "../../components/UserInput";
import { updateStatusSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import { DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID, DEFAULT_LABORATORY_ADMIN_ROLE_ID } from "../../utils/constants";

type FormState = typeof updateStatusSchema.__outputType;
type AdminAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/**
 * Formats an admin's display name as "SURNAME, Given Prefix".
 *
 * Args:
 *   admin: Optional admin record to format.
 *
 * Returns:
 *   A formatted name string (empty segments when missing).
 */
const formatAdminName = (admin?: Users | null) =>
  `${admin?.surname?.toUpperCase() ?? ""}, ${admin?.given_name ?? ""} ${admin?.prefix ?? ""
  }`;

/**
 * Renders a truncated table cell value with a two-line clamp.
 *
 * Args:
 *   text: Cell text to display.
 *
 * Returns:
 *   A Typography element with ellipsis overflow styling.
 */
const renderCellText = (text: string) => (
  <Typography
    className={style.cell}
    sx={{
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: "2",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }}
  >
    {text}
  </Typography>
);

/**
 * Builds the action list for the admin row menu based on active state.
 *
 * Args:
 *   isActive: Whether the admin is currently active.
 *
 * Returns:
 *   The action menu items for the admin row.
 */
const getAdminActions = (isActive: boolean): ActionItem<AdminAction>[] => [
  {
    label: "View",
    value: "view",
    icon: <EyeIcon height={24} width={24} />,
  },
  ...(isActive
    ? ([
      {
        label: "Edit",
        value: "edit",
        icon: <EditIcon height={24} width={24} />,
      },
    ] as ActionItem<AdminAction>[])
    : []),
  {
    label: "Logs",
    value: "logs",
    icon: <img src={Logs} alt="Logs" width={24} />,
  },
  {
    label: isActive ? "Deactivate" : "Activate",
    value: isActive ? "deactivate" : "activate",
    icon: isActive ? (
      <DeactivateIcon height={24} width={24} />
    ) : (
      <PowerSettingsNewIcon height={24} width={24} />
    ),
  },
];

/**
 * Renders the admin list with search, status actions, and navigation to detail/edit.
 *
 * Notes:
 *   - Relies on authenticated user context for health organisation scoping.
 *   - TODO: Confirm whether user role defaults should be sourced from server config.
 *
 * Returns:
 *   The Admin list page component.
 */
const Admin = () => {
  const { userDetails, userRole } = useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<Users | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });

  const healthOrgAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role) => role.name === "Health Organisation Admin"
      )?.id,
    [rolesData]
  );

  const laboratoryAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role) => role.name === "Laboratory Admin"
      )?.id,
    [rolesData]
  );

  // Keep query scoping aligned with auth context and resolved role ids to avoid cross-role leakage.
  const baseQueryParams = useMemo(() => {
    const isLaboratoryAdmin = userRole === "LABORATORY_ADMIN";
    return {
      order_by_columns: "surname",
      order: "Asc",
      is_detailed: true,
      health_org_id: isLaboratoryAdmin
        ? undefined
        : userDetails?.health_org_id ?? 0,
      laboratory_id: isLaboratoryAdmin
        ? userDetails?.laboratory_id ?? 0
        : undefined,
      user_role_id: isLaboratoryAdmin
        ? laboratoryAdminRoleId ?? DEFAULT_LABORATORY_ADMIN_ROLE_ID
        : healthOrgAdminRoleId ?? DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID,
    };
  }, [
    userRole,
    userDetails?.health_org_id,
    userDetails?.laboratory_id,
    healthOrgAdminRoleId,
    laboratoryAdminRoleId,
  ]);

  const [queryParam, setQueryParam] = useState(() => ({
    search_key: "",
    ...baseQueryParams,
  }));
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceRef = useRef<number | null>(null);
  const latestSearchInputRef = useRef("");

  const {
    data: adminsData,
    refetch: refetchAdmins,
    isFetching,
  } = useGetUsersListQuery(queryParam);

  // Ensure the list stays aligned with auth context and resolved role id.
  // Sync query params when auth context or role ids resolve, without resetting the current search term.
  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setQueryParam((prevParams) => ({
      ...prevParams,
      ...baseQueryParams,
      search_key: latestSearchInputRef.current,
    }));
  }, [baseQueryParams]);

  useEffect(() => {
    latestSearchInputRef.current = searchInput;
  }, [searchInput]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleMenu = useCallback(
    (value: AdminAction, admin: Users) => {
      setSelectedAdmin(admin);
      if (value === "deactivate" || value === "activate") {
        setModalVisible(true);
      } else if (value === "view") {
        navigate(`/admins/details/${admin?.id}`);
      } else if (value === "edit") {
        navigate(`/admins/edit/${admin?.id}`);
      } else if (value === "logs") {
        setLogModalVisible(true);
      }
    },
    [navigate]
  );

  const fields = useMemo(
    () => [
      {
        id: "surname",
        label: "Name",
        flex: 2.5,
        sortable: true,
        type: "name",
        render: (item: Users) => renderCellText(formatAdminName(item)),
      },
      {
        id: "mail",
        label: "Email Address",
        flex: 2.5,
        type: "name",
        render: (item: Users) => renderCellText(item?.mail ?? ""),
      },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 1.5,
        sortable: true,
        render: (item: Users) => (
          <StatusBadge isActive={item.is_active ?? false} />
        ),
      },
      {
        id: "logo_s3_key_name",
        label: "Actions",
        type: "action",
        render: (item: Users) => (
          <CustomActionMenu
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleMenu}
            actions={getAdminActions(item.is_active ?? false)}
          />
        ),
      },
    ],
    [handleMenu]
  );

  // Debounce search updates to reduce request churn while typing.
  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchInput(value);
    latestSearchInputRef.current = value;
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: value,
      }));
    }, 300);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Submits an activation or deactivation update for the selected admin.
   *
   * Args:
   *   values: Form values containing the optional deactivation reason.
   *
   * Returns:
   *   A promise that resolves when the update and refetch complete.
   *
   * Raises:
   *   Error: Propagated from the update mutation when the request fails.
   *
   * Notes:
   *   - Requires a selected admin; shows a message if none is selected.
   */
  // Status updates are side-effectful (mutation + refetch) and must guard against missing selection.
  const onSubmit = async (values: FormState) => {
    if (!selectedAdmin?.id) {
      setMessage({
        type: "error",
        text: "Please select a valid admin before updating status.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedAdmin.id,
        is_active: !selectedAdmin?.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
      await refetchAdmins();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the Admin component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchInput}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Button
          variant="contained"
          className={style.addButton}
          label={"Add New Admin"}
          sx={{
            ":hover": { backgroundColor: colors.primary },
          }}
          disabled={!healthOrgAdminRoleId}
          onClick={() => {
            if (!healthOrgAdminRoleId) {
              setMessage({
                type: "error",
                text: "User role is not available yet. Please wait for roles to load.",
              });
              return;
            }
            navigate("add", {
              state: {
                userRole:
                  userRole === "LABORATORY_ADMIN"
                    ? laboratoryAdminRoleId
                    : healthOrgAdminRoleId,
                laboratoryAdminRoleId,
              },
            });
          }}
        />
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={adminsData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(admin: Users) => {
            navigate(`/admins/details/${admin?.id}`);
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchInput}
          onClearSearch={() => setSearchInput("")}
        />
      </Box>
      {modalVisible && selectedAdmin && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedAdmin.is_active ? "deactivate " : "activate "
            }"${formatAdminName(selectedAdmin)}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            // Deactivation requires a reason; activation does not.
            if (selectedAdmin?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedAdmin}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};
export default Admin;
